import {authService} from "../../client/connection/services/AuthService.js";
import {userService} from "../../client/connection/services/UserService.js";
import {UserPreferences, TopUpPayment, Payment, Subscription, Donation} from "../State";
import {iFrameConnection} from "./IFrameConnection";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages";
import {iFrameState} from "./IFrameState";
import {isDeepEqual, isString} from "../UtilsLib";
import {apiUpdatePreferences} from "../../client/state/UserPreferencesStore";
import {Dispatchable} from "../../stem-core/base/Dispatcher";
import {iFrameMerchantService} from "./IframeMerchantService";
import {iFrameUserDataService} from "./IFrameUserDataService";
import {MerchantAudienceMember} from "../../client/state/merchant/MerchantAudienceMemberStore";
import {TimeUnit} from "../../stem-core/time/Duration";
import {EMPTY_USER_DATA, TNR_ALIAS, type SdkUserData} from "../../blink-sdk/Constants";
import {MerchantUser} from "../../client/state/merchant/MerchantUserStore";
import {StemDate} from "../../stem-core/time/Date.js";
import {Money} from "../../stem-core/localization/Money";


// TODO: Rename this. Maybe split the functionality.
class IFrameBlinkService extends Dispatchable {
    lastSdkUserData: SdkUserData | null = null;

    init() {
        this.updateSdkUserData();
        this.addListeners();
        this.addConnectionDispatchers();
    }

    addListeners() {
        // We need the sdk user data to be updated when we get all those flow checkpoint events. That is why we can't
        // rely on the iFrameUserDataService which sends those change events in an desynchronized way.
        const pushDataToSDK = () => this.updateSdkUserData();

        Subscription.addChangeListener(pushDataToSDK);
        Donation.addChangeListener(pushDataToSDK);
        UserPreferences.addChangeListener(pushDataToSDK);
        MerchantUser.addChangeListener(pushDataToSDK);
        MerchantAudienceMember.addChangeListener(pushDataToSDK)

        iFrameUserDataService.addChangeListener(pushDataToSDK);
        iFrameState.addChangeListener(pushDataToSDK);

        authService.onAuthenticationChange(pushDataToSDK);
    }

    addConnectionDispatchers() {
        TopUpPayment.addCreateListener(topUp => this.showPaymentAnimation(topUp.getAmount()));
        Payment.addCreateListener(purchase => this.showPaymentAnimation(purchase.getDebitedAmount()));
    }

    updateSdkUserData() {
        const sdkUserData = this.getSdkUserData();
        if (isDeepEqual(sdkUserData, this.lastSdkUserData)) {
            return;
        }
        this.lastSdkUserData = sdkUserData;
        iFrameConnection.sendToSDK(IFrameMessages.UPDATE_SDK_USER_DATA, {userData: sdkUserData});
    }

    getSerializedSubscription() {
        const subscription = iFrameUserDataService.getActiveSubscription();
        if (subscription) {
            return subscription.serializeForSDK();
        }
        const emailAddress = userService.getUserEmail();
        // TODO @flow Subscription rules based on email.
        if (iFrameMerchantService.isMerchant(TNR_ALIAS) && isString(emailAddress) && emailAddress.indexOf("@tnr.com") >= 0) {
            const fakeSubscriptionStartDate = Date.now();

            return {
                id: "fake-tnr",
                currencyCode: "usd",
                deliveryAddressId: null,
                blinkSignature: null,
                nextPaymentAttempt: null,
                userId: userService.getUserId(),
                amount: 20000000,
                createdAt: (new StemDate(fakeSubscriptionStartDate)).format(),
                canceledAt: null,
                activeUntil: (new StemDate(fakeSubscriptionStartDate)).addUnit(TimeUnit.MONTH).format(),
            };
        }
        return null;
    }

    getSdkUserData(): SdkUserData {
        const defaultSdkUserData = EMPTY_USER_DATA;
        const userId = userService.getUserId();
        const preferences = UserPreferences.getPreferences();
        const merchantUser = MerchantUser.getByUserAndMerchant(userId, iFrameMerchantService.merchantId);

        if (!authService.isAuthenticated() || !preferences) {
            return defaultSdkUserData;
        }

        const {showOnboarding} = preferences;
        if (showOnboarding) {
            apiUpdatePreferences({showOnboarding: false}).then();
        }

        const user = userService.getUser();
        const merchant = iFrameMerchantService.getMerchant();
        const merchantCurrency = merchant?.getCurrency();
        const activeRecurringDonation = iFrameUserDataService.getActiveRecurringDonation();
        const latestOneTimeDonation = iFrameUserDataService.getLatestOneTimeDonation();
        const activeNewsletters = MerchantAudienceMember.filter(member => member.isSubscribed() && member.merchantId === iFrameMerchantService.merchantId);
        const lifetimeRevenue = merchantCurrency && new Money(merchantUser?.lifetimeRevenue || 0, merchantCurrency).toRichJSON();
        const lifetimeDonated = merchantCurrency && new Money(merchantUser?.lifetimeDonatedAmount || 0, merchantCurrency).toRichJSON();

        // When adding a field here you should also add one in EMPTY_USER_DATA, for unauthenticated users
        return {
            userId,
            email: user?.email,
            emailVerified: user?.isEmailVerified,
            firstName: user?.firstName,
            lastName: user?.lastName,
            fullName: userService.getUserName(),
            canReceivePromotionalEmails: merchantUser?.canReceivePromotionalEmails,
            lifetimeRevenue,
            lifetimeDonated,
            alwaysShowBalance: preferences.shouldShowBalanceInline(),
            subscription: this.getSerializedSubscription(),
            showOnboarding,
            activeRecurringDonation: activeRecurringDonation?.serializeForSDK(),
            latestOneTimeDonation: latestOneTimeDonation?.serializeForSDK(),
            activeNewsletters: activeNewsletters.map(audienceMember => audienceMember.serializeForSDK()),
            newsletterIds: activeNewsletters.map(newsletterMember => newsletterMember.audienceId),
            metadata: merchantUser?.metadata || {},
        };
    }

    showPaymentAnimation(paymentAmount: Money) {
        const userPreferences = UserPreferences.getPreferences();
        if (paymentAmount.getAmount() > 0 && userPreferences?.showPaymentAnimation) {
            iFrameConnection.sendToSDK(IFrameMessages.SESSION_PAYMENT_MADE);
        }
    }
}

export const iFrameBlinkService = new IFrameBlinkService();
